import { useAuth } from 'contexts/auth';
import { useApp } from 'contexts/app';
import { useWebsocket } from 'contexts/websocket';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPageLabel, getPageSuggestions } from 'components/Chat/instructor-hugo/pageConfig';
import chatService from 'services/chat';
import {
    ChatRole,
    CHAT_PAGE_SIZE,
    INSTRUCTOR_HUGO_CHAT_TYPE,
    NEW_THREAD_VALUE,
    THREAD_SUMMARY_LIMIT,
} from 'utils/chat/chatConstants';
import {
    buildInstructorHugoContext,
    buildSessionContext,
    pruneEmptyContext,
    ScopeTypes,
} from 'utils/chat/instructorHugoContext';
import { appendPersistedMessage, buildChatReconciliationPayload } from 'utils/chat/chatState';
import { v4 as uuid } from 'uuid';

const getFirstName = ({ auth, currentUser }) => {
    const name = currentUser?.first_name || auth?.first_name || currentUser?.display_name || auth?.display_name || '';
    return name.trim().split(/\s+/)[0] || '';
};

const createLocalChatShell = ({ auth, scope }) => {
    const timestamp = Date.now();
    return {
        id: null,
        local_id: uuid(),
        active: true,
        created_on: timestamp,
        modified_on: timestamp,
        chat_type: INSTRUCTOR_HUGO_CHAT_TYPE,
        owner_account_id: auth?.owner_account_id,
        scope_type: scope.type,
        course_id: scope.course_id,
        assignment_id: scope.assignment_id,
        is_processing: false,
        messages: [],
        assistant_interactions: [],
        messages_version: 0,
        read_state: [],
    };
};

const getRouteChatKey = ({ auth, sessionContext }) => {
    const { pageContext, scope } = sessionContext;

    return [
        auth?.owner_account_id || '',
        scope.type || '',
        scope.course_id || '',
        scope.assignment_id || '',
        pageContext.page_type || '',
        pageContext.active_tab || '',
    ].join(':');
};

const buildThreadFilter = ({ auth }) => ({
    active: true,
    chat_type: INSTRUCTOR_HUGO_CHAT_TYPE,
    owner_account_id: auth?.owner_account_id,
});

const ActiveThreadReconciliationIntervalMs = 2000;

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const getThreadLabel = (chat) => {
    const latestMessage = chat.latest_message || (chat.messages || []).slice(-1)[0];
    return latestMessage?.message_text?.trim();
};

const shouldShowPageSuggestions = (chat) => {
    return !chat?.id && !(chat?.messages || []).some((message) => message.role === ChatRole.USER);
};

const getThreadScopeLabel = (chat, t) => {
    const scopes = [];

    if ([ScopeTypes.COURSE, ScopeTypes.ASSIGNMENT].includes(chat.scope_type)) {
        scopes.push(chat.course_name || t('hugo-chat.course', 'Course'));
    }

    if (chat.scope_type === ScopeTypes.ASSIGNMENT) {
        scopes.push(chat.assignment_name || t('hugo-chat.assignment', 'Assignment'));
    }

    if (chat.scope_type === ScopeTypes.USER) {
        const pageType = chat.page_type || chat.latest_message?.page_context?.page_type;
        scopes.push(getPageLabel(pageType, t));
    }

    return scopes.join(' > ');
};

const useInstructorHugoChat = ({ isOpen, pathname }) => {
    const { auth, currentUser } = useAuth();
    const {
        accountUsers,
        assignments,
        course,
        currentAssignment,
        instructorHugoPageView,
        previewSubmissions,
        rubrics,
        submissions,
    } = useApp();
    const { t } = useTranslation();
    const { isConnected, onMessage, setPingPayload } = useWebsocket();

    const derivedSessionContext = useMemo(() => buildSessionContext({ auth, pathname }), [auth, pathname]);
    const currentRouteChatKey = useMemo(
        () => getRouteChatKey({ auth, sessionContext: derivedSessionContext }),
        [auth, derivedSessionContext]
    );
    const [activeRouteChatKey, setActiveRouteChatKey] = useState(currentRouteChatKey);
    const [sessionContext, setSessionContext] = useState(derivedSessionContext);

    const { pageContext, routeContext, scope } = sessionContext;
    const messageContext = useMemo(
        () =>
            buildInstructorHugoContext({
                accountUsers,
                assignments,
                course,
                currentAssignment,
                instructorHugoPageView,
                previewSubmissions,
                routeContext,
                rubrics,
                submissions,
            }),
        [
            accountUsers,
            assignments,
            course,
            currentAssignment,
            instructorHugoPageView,
            previewSubmissions,
            routeContext,
            rubrics,
            submissions,
        ]
    );
    const outgoingPageContext = useMemo(
        () =>
            pruneEmptyContext({
                ...pageContext,
                page_type: messageContext?.page?.type || pageContext.page_type,
                active_tab: messageContext?.page?.view || pageContext.active_tab,
            }),
        [messageContext, pageContext]
    );
    const [chat, setChat] = useState(() => createLocalChatShell({ auth, scope: derivedSessionContext.scope }));
    const [threadSummaries, setThreadSummaries] = useState([]);
    const [isLoadingThreads, setIsLoadingThreads] = useState(false);
    const [isSwitchingThread, setIsSwitchingThread] = useState(false);
    const chatRef = useRef(chat);
    const isRefreshingActiveThreadRef = useRef(false);
    const isChatRouteSynced = activeRouteChatKey === currentRouteChatKey;
    const selectedThreadValue = chat.id || NEW_THREAD_VALUE;

    useEffect(() => {
        chatRef.current = chat;
    }, [chat]);

    const loadThreadSummaries = useCallback(async () => {
        if (!auth?.owner_account_id) {
            setThreadSummaries([]);
            return [];
        }

        setIsLoadingThreads(true);
        try {
            const summaries = await chatService.listChats(buildThreadFilter({ auth }), {
                includeMessages: false,
                includeNames: true,
                orderBy: 'modified_on desc',
                top: THREAD_SUMMARY_LIMIT,
            });
            setThreadSummaries(summaries || []);
            return summaries || [];
        } catch {
            setThreadSummaries([]);
            return [];
        } finally {
            setIsLoadingThreads(false);
        }
    }, [auth]);

    useEffect(() => {
        if (!isOpen) return;

        loadThreadSummaries();

        if (isChatRouteSynced) return;

        setActiveRouteChatKey(currentRouteChatKey);
        setSessionContext(derivedSessionContext);
        setChat(createLocalChatShell({ auth, scope: derivedSessionContext.scope }));
    }, [auth, currentRouteChatKey, derivedSessionContext, isChatRouteSynced, isOpen, loadThreadSummaries]);

    const startNewThread = useCallback(() => {
        setChat(createLocalChatShell({ auth, scope }));
    }, [auth, scope]);

    const switchThread = useCallback(
        async (threadId) => {
            if (!threadId || threadId === NEW_THREAD_VALUE) {
                startNewThread();
                return;
            }

            setIsSwitchingThread(true);
            try {
                const nextChat = await chatService.getChat(threadId, {
                    messages_top: CHAT_PAGE_SIZE,
                });
                if (!nextChat?.id) return;
                setChat(nextChat);
            } finally {
                setIsSwitchingThread(false);
            }
        },
        [startNewThread]
    );

    const threadListAdapter = useMemo(
        () => ({
            threadId: chat.id || undefined,
            isLoading: isLoadingThreads,
            threads: threadSummaries.map((thread) => ({
                id: thread.id,
                title: getThreadLabel(thread),
                status: 'regular',
            })),
            onSwitchToNewThread: startNewThread,
            onSwitchToThread: switchThread,
        }),
        [chat.id, isLoadingThreads, startNewThread, switchThread, threadSummaries]
    );

    const refreshActiveThread = useCallback(async (chatId, { shouldIgnore = () => false } = {}) => {
        if (!chatId) return null;
        if (isRefreshingActiveThreadRef.current) return null;

        isRefreshingActiveThreadRef.current = true;
        try {
            const persistedChat = await chatService.getChat(chatId, {
                messages_top: CHAT_PAGE_SIZE,
            });
            if (!persistedChat?.id) return null;
            if (shouldIgnore()) return null;

            setChat((currentChat) => {
                if (currentChat?.id !== persistedChat.id) return currentChat;
                return persistedChat;
            });

            return persistedChat;
        } finally {
            isRefreshingActiveThreadRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!isOpen || !chat.id) return;

        let shouldIgnore = false;
        refreshActiveThread(chat.id, { shouldIgnore: () => shouldIgnore }).catch(() => {
            // A later open, switch, or processing poll can reconcile the thread.
        });

        return () => {
            shouldIgnore = true;
        };
    }, [chat.id, isOpen, refreshActiveThread]);

    useEffect(() => {
        const cleanup = setPingPayload(() => {
            if (!isOpen) return {};
            return buildChatReconciliationPayload(chatRef.current);
        });

        return cleanup;
    }, [isOpen, setPingPayload]);

    useEffect(() => {
        const cleanup = onMessage(async (message) => {
            if (message.type !== 'pong' || !Array.isArray(message.chat_reconciliations)) return;

            const currentChatId = chatRef.current?.id;
            if (!currentChatId) return;

            const reconciliation = message.chat_reconciliations.find(({ chat_id }) => chat_id === currentChatId);
            if (!reconciliation?.out_of_sync) return;

            try {
                const persistedChat = await refreshActiveThread(currentChatId);
                if (persistedChat) loadThreadSummaries();
            } catch {
                // A later ping, open, or processing refresh can reconcile the thread.
            }
        });

        return cleanup;
    }, [loadThreadSummaries, onMessage, refreshActiveThread]);

    useEffect(() => {
        if (!isConnected || !isOpen) return;

        const currentChatId = chatRef.current?.id;
        if (!currentChatId) return;

        refreshActiveThread(currentChatId).catch(() => {
            // Best-effort parity with assignment chat reconciliation.
        });
    }, [isConnected, isOpen, refreshActiveThread]);

    useEffect(() => {
        if (!isOpen || !chat.id || !chat.is_processing) return;

        let shouldIgnore = false;
        const refreshProcessingThread = async () => {
            try {
                const persistedChat = await refreshActiveThread(chat.id, {
                    shouldIgnore: () => shouldIgnore,
                });
                if (persistedChat && !persistedChat.is_processing) {
                    loadThreadSummaries();
                }
            } catch {
                // Keep polling while the local thread is still marked as processing.
            }
        };

        refreshProcessingThread();
        const intervalId = setInterval(refreshProcessingThread, ActiveThreadReconciliationIntervalMs);

        return () => {
            shouldIgnore = true;
            clearInterval(intervalId);
        };
    }, [chat.id, chat.is_processing, isOpen, loadThreadSummaries, refreshActiveThread]);

    const reconcilePersistedAssistantMessage = useCallback(async ({ chatId, expectedRequestedInteractionId, messageId }) => {
        if (!chatId || !messageId) return;

        for (const delay of [0, 250, 750, 1500]) {
            if (delay) await wait(delay);
            try {
                const persistedChat = await chatService.getChat(chatId, { messages_top: CHAT_PAGE_SIZE });
                const persistedMessage = (persistedChat?.messages || []).find(({ id }) => id === messageId);
                if (!persistedMessage) continue;
                if (
                    expectedRequestedInteractionId &&
                    persistedMessage.requested_interaction?.id !== expectedRequestedInteractionId
                ) {
                    continue;
                }
                setChat(persistedChat);
                return;
            } catch {
                // A later retry or reopening the thread will reconcile from persisted history.
            }
        }
    }, []);

    const buildOutgoingRequest = useCallback(
        ({ chatId, requestId, userMessage }) => {
            return {
                type: 'send_ai_message',
                id: requestId,
                request_id: requestId,
                chat_type: INSTRUCTOR_HUGO_CHAT_TYPE,
                chat_id: chatId || null,
                scope,
                context: messageContext,
                page_context: outgoingPageContext,
                created_on: userMessage.created_on,
                message_id: userMessage.id,
                message_text: userMessage.message_text,
                ...(userMessage.interaction_response ? { interaction_response: userMessage.interaction_response } : {}),
            };
        },
        [messageContext, outgoingPageContext, scope]
    );

    const onChatRequestStateChange = useCallback(
        (event) => {
            if (!event?.type) return;

            if (event.type === 'user_message_created') {
                setChat((currentChat) => {
                    const userMessage = {
                        ...event.userMessage,
                        context: messageContext,
                        page_context: outgoingPageContext,
                        role: ChatRole.USER,
                    };
                    const nextChat = appendPersistedMessage(currentChat, userMessage);

                    return {
                        ...nextChat,
                        is_processing: true,
                    };
                });
                return;
            }

            if (event.type === 'assistant_message_created') {
                const persistedChatId = event.payload?.chat_id;

                setChat((currentChat) => {
                    const nextChat = appendPersistedMessage(currentChat, event.assistantMessage);

                    return {
                        ...nextChat,
                        id: persistedChatId || currentChat.id,
                        is_processing: false,
                    };
                });
                reconcilePersistedAssistantMessage({
                    chatId: persistedChatId,
                    expectedRequestedInteractionId: event.assistantMessage.requested_interaction?.id,
                    messageId: event.assistantMessage.id,
                });
                loadThreadSummaries();
                return;
            }

            if (event.type === 'request_failed') {
                setChat((currentChat) => ({
                    ...currentChat,
                    is_processing: false,
                }));
            }
        },
        [loadThreadSummaries, messageContext, outgoingPageContext, reconcilePersistedAssistantMessage]
    );

    const onDeleteThread = useCallback(
        async (threadId) => {
            if (!threadId) return;
            try {
                await chatService.deleteChat(threadId);
                setThreadSummaries((currentSummaries) => currentSummaries.filter((thread) => thread.id !== threadId));

                if (chat.id === threadId) {
                    startNewThread();
                }
            } catch (error) {
                console.error('Failed to delete thread', error);
            }
        },
        [chat.id, startNewThread]
    );

    const pageSuggestions = useMemo(
        () => getPageSuggestions(outgoingPageContext.page_type, t),
        [outgoingPageContext.page_type, t]
    );
    const showPageSuggestions = shouldShowPageSuggestions(chat);

    return {
        buildOutgoingRequest,
        chat,
        isChatRouteSynced,
        isBootstrapping: false,
        onChatRequestStateChange,
        onDeleteThread,
        showSuggestions: showPageSuggestions,
        suggestions: pageSuggestions,
        suggestionsTitle: t('hugo-chat.quick-ideas-title', 'Quick ideas to get you started'),
        threadListAdapter,
        threadSwitcher: {
            disabled: isSwitchingThread,
            isLoading: isLoadingThreads,
            items: threadSummaries.map((thread) => ({
                id: thread.id,
                label: getThreadLabel(thread),
                scopeLabel: getThreadScopeLabel(thread, t),
            })),
            newThreadValue: NEW_THREAD_VALUE,
            selectedValue: selectedThreadValue,
            onSelect: switchThread,
            onStartNew: startNewThread,
        },
        viewModel: {
            composer: {
                placeholder: 'Help me with this page',
            },
            emptyState: {
                body: t('hugo-chat.greeting-assignment-help', 'How can I help with your assignment?'),
                firstName: getFirstName({ auth, currentUser }),
                isVisible: showPageSuggestions,
            },
            header: {
                title: 'Hugo',
            },
        },
    };
};

export default useInstructorHugoChat;
